import * as React from "react";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_atoms/command";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";

import { createOptionFilter } from "./create-option-filter";

export type ComboboxOption<TData> = {
  id: string;
  value: string;
  original: TData | null;
};

export interface ComboboxProps<TData> extends PropsWithChildren {
  options: ComboboxOption<TData>[];
  renderItem: (status: ComboboxOption<TData>) => React.ReactElement;
  onSelect?: (status: ComboboxOption<TData> | null) => void;
  placeholder?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  asChild?: boolean;
  allowDeselect?: boolean;
  modal?: boolean;
  isReadOnly?: boolean;
  disabled?: boolean;
  renderHeader?: () => React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  closeOnSelect?: boolean;
  checkedIdList?: readonly string[];
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  value?: ComboboxOption<TData> | null;
}

export const Combobox = <TData,>({
  options,
  placeholder,
  renderItem,
  onSelect,
  asChild,
  side,
  align,
  children,
  allowDeselect,
  modal,
  isReadOnly,
  disabled,
  renderHeader,
  open,
  onOpenChange,
  closeOnSelect = true,
  checkedIdList,
  searchQuery,
  onSearchChange,
  value,
}: ComboboxProps<TData>) => {
  const { t } = useTranslation("common");
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [selectedOption, setSelectedOption] = useState<ComboboxOption<TData> | null>(null);
  const filterOption = useMemo(() => createOptionFilter(options), [options]);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;
  const handleOpenChange = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  // Sync selectedOption with value prop when provided
  useEffect(() => {
    if (value !== undefined) {
      setSelectedOption(value);
    }
  }, [value]);

  function handleSelectOption(option: ComboboxOption<TData>) {
    if (!allowDeselect && selectedOption && selectedOption.id === option.id) {
      return; // nothing changed
    }
    setSelectedOption(option);
    if (onSelect) {
      onSelect(option);
    }
    if (closeOnSelect) {
      handleOpenChange(false);
    }
  }

  if (isReadOnly) {
    return <div className="pointer-events-none min-w-0 max-w-full">{children}</div>;
  }

  return (
    <Popover open={currentOpen} onOpenChange={handleOpenChange} modal={modal ?? false}>
      <PopoverTrigger asChild={asChild} disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverPortalContent className="pointer-events-auto p-0" side={side ?? "right"} align={align ?? "start"}>
        <Command filter={filterOption}>
          <CommandInput
            placeholder={placeholder ?? t("combobox.placeholder")}
            value={searchQuery}
            onValueChange={onSearchChange}
          />

          {renderHeader && renderHeader()}
          <CommandList
            aria-multiselectable={checkedIdList ? true : undefined}
            escapeScrollLock
            style={{ maxHeight: "min(300px, var(--radix-popover-content-available-height, 300px))" }}
          >
            <CommandEmpty>{t("search.noResults")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  aria-checked={checkedIdList?.includes(option.id)}
                  onSelect={() => handleSelectOption(option)}
                  className="p-0"
                >
                  {renderItem(option)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverPortalContent>
    </Popover>
  );
};
